import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import BrandManageModal from '@features/BrandManageModal';
import { Typography } from '@components/Typography';
import { Select } from '@components/Select';
import { useTranslation, useLocations } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { brandsActions } from '@actions';
import { FORM_TYPE } from '@constants/brands';

const Wrapper = styled('div')({
    display: 'flex',
    gap: 24,
    marginTop: -8,
    marginBottom: 8,
    '@media (max-width: 768px)': {
        marginTop: -16,
    },
});

const StyledRadioGroup = styled(RadioGroup)({
    flexDirection: 'row',
    alignItems: 'center',
    '& .MuiFormControlLabel-root': {
        marginRight: 0,
        marginLeft: '-3px !important',
    },
});

export const LOCATION_TYPE = {
    LOCATION: '0',
    BRAND: '1',
};

/**
 *
 * @param {Array} brandsOptions
 * @param {Array} locationsOptions
 * @param {Object} data - entity from edit modals
 * @param {boolean} disabled
 * @param {boolean} isView - allow low access level users see locations nad brand assets
 * @param {Function} updatedLocationsCb - for setting the depending on location values (for brands - first brand location id)
 */
const ComboLocationSelect = ({
    brandsOptions,
    locationsOptions,
    data,
    disabled,
    isView,
    module,
    updatedLocationCb = () => {},
    updatedLocationTypeCb = () => {},
}) => {
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });

    const brands = useSelector(state => state.brands.data);
    const { control, watch, setValue } = useFormContext();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { hasFullAccess, hasFullSettings, hasAnyBrandScope, getWritableLocationIds, getWritableBrandIds } =
        usePermissions();
    const { hasSingleLocation, singleLocationId } = useLocations();

    const canManageLocations = hasFullAccess('locations');

    const filteredLocationsOptions = useMemo(() => {
        if (!module || isView) return locationsOptions;
        const writableIds = getWritableLocationIds(module);
        if (writableIds === null) return locationsOptions;
        return locationsOptions
            .map(group => ({
                ...group,
                children: group.children.filter(loc => writableIds.has(loc.value)),
            }))
            .filter(group => group.children.length > 0);
    }, [locationsOptions, module, isView, getWritableLocationIds]);

    const filteredBrandsOptions = useMemo(() => {
        if (!module || isView) return brandsOptions;
        const writableIds = getWritableBrandIds(module);
        if (writableIds === null) return brandsOptions;
        return brandsOptions.filter(b => writableIds.has(b.value));
    }, [brandsOptions, module, isView, getWritableBrandIds]);

    const autoSelectSingleLocation = useMemo(() => {
        if (!hasSingleLocation || !singleLocationId || isView) return false;
        return filteredLocationsOptions.some(group => group.children.some(c => c.value === singleLocationId));
    }, [hasSingleLocation, singleLocationId, isView, filteredLocationsOptions]);

    const locationType = watch('locationType');
    const brandAccess = watch('brandAccess');
    const valuesAlreadySetToForm = watch('valuesAlreadySetToForm');

    const hasBrandAccess = module ? filteredBrandsOptions.length > 0 : canManageLocations && hasAnyBrandScope;

    useEffect(() => {
        setValue('brandAccess', hasBrandAccess);

        if (autoSelectSingleLocation) {
            setValue('locationType', LOCATION_TYPE.LOCATION, { shouldValidate: true });
            setValue('locationId', singleLocationId, { shouldValidate: true });
            setValue('brandId', '', { shouldValidate: true });
            setValue('valuesAlreadySetToForm', true);
            // On edit the entity already belongs to this one location, so skip the callback
            // that resets location-dependent fields — there's no real location change to react to.
            if (!data) updatedLocationCb(singleLocationId);
            return;
        }

        if (!hasBrandAccess && !isView) {
            setValue('locationType', LOCATION_TYPE.LOCATION, { shouldValidate: true });
        }

        if (data && !valuesAlreadySetToForm) {
            setValue('brandId', data?.brandId || '', { shouldValidate: true });
            setValue('locationId', data?.locationId || '', { shouldValidate: true });
            setValue('locationType', data?.locationId ? LOCATION_TYPE.LOCATION : LOCATION_TYPE.BRAND, {
                shouldValidate: true,
            });
            setValue('valuesAlreadySetToForm', true);
        }
    }, [setValue, hasBrandAccess, isView, data, valuesAlreadySetToForm, autoSelectSingleLocation, singleLocationId]);

    const refetchBrandsCallback = () => dispatch(brandsActions.getAll());

    if (autoSelectSingleLocation) return null;

    return brandAccess || isView ? (
        <div className="combo-location-select">
            <Controller
                control={control}
                name="locationType"
                render={() => (
                    <Wrapper className="location-type">
                        {[
                            { id: LOCATION_TYPE.BRAND, name: t('common.brand') },
                            { id: LOCATION_TYPE.LOCATION, name: t('common.location') },
                        ].map(type => (
                            <Fragment key={type.id}>
                                <StyledRadioGroup
                                    value={locationType}
                                    onChange={e => {
                                        setValue('locationType', e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                        updatedLocationTypeCb();
                                    }}
                                >
                                    <FormControlLabel
                                        value={type.id}
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'var(--text-secondary)',
                                                    padding: '6px',
                                                    '&.Mui-checked': { color: 'var(--primary-main)' },
                                                }}
                                                disabled={disabled}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                                {type.name}
                                            </Typography>
                                        }
                                        disabled={disabled}
                                    ></FormControlLabel>
                                </StyledRadioGroup>
                            </Fragment>
                        ))}
                    </Wrapper>
                )}
            />
            {locationType === LOCATION_TYPE.BRAND && (
                <Controller
                    control={control}
                    name="brandId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            onChange={({ target: { value } }) => {
                                if (value !== 'create-new') {
                                    setValue('brandId', value, { shouldValidate: true, shouldDirty: true });
                                    setValue('locationId', '', { shouldDirty: true, shouldValidate: true });
                                    const brandLocationId = brands.find(b => b.id === value)?.locations[0]?.id;
                                    updatedLocationCb(brandLocationId);
                                }
                            }}
                            ref={ref}
                            label={t('common.brand')}
                            fullWidth
                            required
                            options={filteredBrandsOptions}
                            disabled={disabled}
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            creatable={hasFullSettings()}
                            creatableText={t('SettingsBrands.createNewBrand')}
                            creatableClick={() => setModal({ open: true, type: FORM_TYPE.NEW })}
                        />
                    )}
                />
            )}
            {locationType === LOCATION_TYPE.LOCATION && (
                <Controller
                    control={control}
                    name="locationId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                setValue('locationId', value, { shouldValidate: true, shouldDirty: true });
                                setValue('brandId', '', { shouldDirty: true, shouldValidate: true });
                                updatedLocationCb(value);
                            }}
                            options={filteredLocationsOptions}
                            required
                            label={t('common.location')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            groupBased
                            disabled={disabled}
                        />
                    )}
                />
            )}
            {modal.open ? (
                <BrandManageModal
                    modal={modal}
                    onClose={() => setModal({ open: false, type: FORM_TYPE.NEW })}
                    refetchCallback={refetchBrandsCallback}
                />
            ) : null}
        </div>
    ) : (
        <div className="combo-location-select">
            <Controller
                control={control}
                name="locationId"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            setValue('locationId', value, { shouldValidate: true, shouldDirty: true });
                            setValue('brandId', '', { shouldDirty: true, shouldValidate: true });
                            updatedLocationCb(value);
                        }}
                        options={filteredLocationsOptions}
                        required
                        label={t('common.location')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        groupBased
                        disabled={disabled}
                    />
                )}
            />
        </div>
    );
};

export default ComboLocationSelect;
